import { createContext, use } from "react";
import type { RelatedDetailType } from "@/components/shared/relationships/RelatedDetailSurface";

export interface PeekEntity {
  id: string;
  label?: string;
  type: RelatedDetailType | "account" | "expense" | "product" | "shipment" | "vendor-credit";
}

export interface EntityPeekContextValue {
  /** Pops the top entity off the stack, revealing the previous one (if any). */
  back: () => void;
  /** Clears the stack, closing the dialog. */
  close: () => void;
  /** Resets the stack to a single entity, opening (or replacing) the peek. */
  open: (entity: PeekEntity) => void;
  /** Appends an entity on top of the stack, chaining navigation. */
  push: (entity: PeekEntity) => void;
  stack: PeekEntity[];
}

/**
 * The context lives apart from both the provider and the links that consume it
 * because `EntityPeekStack` renders `EntityLink`, and `EntityLink` needs these
 * controls. Holding them in either component makes the two import each other.
 */
export const EntityPeekContext = createContext<EntityPeekContextValue | null>(null);

/** Returns the current entity peek stack controls from the provider context. */
export function useEntityPeek(): EntityPeekContextValue {
  const context = use(EntityPeekContext);
  if (!context) {
    throw new Error("useEntityPeek must be used within an EntityPeekProvider");
  }
  return context;
}
